// TimelineTab.tsx — Hành trình chăm sóc lịch hẹn (API /appointments/{id}/care-logs)
import { useEffect, useState } from "react";
import {
    Button,
    Card,
    Input,
    Select,
    Tag,
    Dropdown,
    Modal,
    Spin,
    message,
} from "antd";
import {
    Paperclip,
    Save,
    PhoneCall,
    MessageCircle,
    Users,
    Mail,
    Star,
    User,
    PencilLine,
    MoreVertical,
} from "lucide-react";
import dayjs from "dayjs";
import AudioPlayer from "./AudioPlayer";
import {
    getAppointmentCareLogs,
    createAppointmentCareLog,
    updateAppointmentCareLog,
    deleteAppointmentCareLog,
    AppointmentCareLog,
    CARE_LOG_TYPES,
} from "../../api/AppointmentCare";

const { TextArea } = Input;

// Icon + màu theo loại chăm sóc (1=Gọi điện, 2=Nhắn tin, 3=Gặp trực tiếp, 4=Email, 5=Khác)
const TYPE_META: Record<number, { icon: any; dot: string; border: string; text: string }> = {
    1: { icon: PhoneCall, dot: "bg-green-500", border: "border-green-300", text: "text-green-500" },
    2: { icon: MessageCircle, dot: "bg-blue-500", border: "border-blue-300", text: "text-blue-500" },
    3: { icon: Users, dot: "bg-purple-500", border: "border-purple-300", text: "text-purple-500" },
    4: { icon: Mail, dot: "bg-cyan-500", border: "border-cyan-300", text: "text-cyan-500" },
    5: { icon: Star, dot: "bg-slate-500", border: "border-slate-300", text: "text-slate-500" },
};

const formatTime = (value: string) => {
    if (!value) return "";
    const d = dayjs(value);
    return d.isSame(dayjs(), "day") ? `Hôm nay - ${d.format("HH:mm")}` : d.format("DD/MM/YYYY - HH:mm");
};

export default function TimelineTab({ appointmentId }: { appointmentId?: number }) {
    const DEFAULT_TYPE = 2; // mặc định gửi loại "Nhắn tin" (phần chọn loại đang ẩn theo yêu cầu)
    const [note, setNote] = useState("");
    const [type, setType] = useState(DEFAULT_TYPE);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    const [logs, setLogs] = useState<AppointmentCareLog[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchLogs = () => {
        if (!appointmentId) { setLogs([]); return; }
        setLoading(true);
        getAppointmentCareLogs(appointmentId).then((res) => {
            setLogs(res.status ? res.items : []);
            setLoading(false);
        });
    };

    useEffect(() => {
        setNote("");
        setType(DEFAULT_TYPE);
        setEditingId(null);
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId]);

    const handleSave = async () => {
        if (!appointmentId) return;
        if (!note.trim()) {
            message.warning("Vui lòng nhập nội dung chăm sóc");
            return;
        }
        setSaving(true);
        const res = editingId
            ? await updateAppointmentCareLog(appointmentId, editingId, { content: note.trim(), type })
            : await createAppointmentCareLog(appointmentId, { content: note.trim(), type });
        setSaving(false);
        if (res.status) {
            message.success(res.msg || (editingId ? "Cập nhật hành trình chăm sóc thành công" : "Thêm hành trình chăm sóc thành công"));
            setNote("");
            setType(DEFAULT_TYPE);
            setEditingId(null);
            fetchLogs();
        } else {
            message.error(res.msg || "Lưu thất bại");
        }
    };

    const handleEdit = (item: AppointmentCareLog) => {
        setEditingId(item.id);
        setNote(item.content);
        setType(item.type);
    };

    const handleDelete = async (item: AppointmentCareLog) => {
        if (!appointmentId) return;
        const res = await deleteAppointmentCareLog(appointmentId, item.id);
        if (res.status) {
            message.success(res.msg || "Xóa hành trình chăm sóc thành công");
            setLogs((prev) => prev.filter((l) => l.id !== item.id));
            if (editingId === item.id) { setEditingId(null); setNote(""); setType(DEFAULT_TYPE); }
        } else {
            message.error(res.msg || "Xóa thất bại");
        }
    };

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-[#1f1f1f]">

            {/* FORM */}

            <div className="p-5">

                <Card
                    className="rounded-xl"
                    styles={{
                        body: {
                            padding: 18,
                        },
                    }}
                >

                    <div className="flex items-center gap-2 mb-4">

                        <PencilLine
                            size={16}
                            className="text-blue-500"
                        />

                        <h3 className="text-[18px] font-semibold text-000000e0">
                            {editingId ? "Chỉnh sửa ghi chú chăm sóc" : "Thêm ghi chú chăm sóc"}
                        </h3>

                    </div>

                    {/* Ẩn phần chọn loại chăm sóc theo yêu cầu — luôn gửi mặc định loại "Nhắn tin" (type 2)
                    <div className="mb-2 text-[13px] text-slate-500">

                        Loại chăm sóc (Phân loại)

                    </div>

                    <Select
                        className="w-[260px]"
                        size="middle"
                        value={type}
                        onChange={setType}
                        options={CARE_LOG_TYPES}
                    /> */}

                    <TextArea
                        rows={4}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Nhập nội dung trao đổi (VD: Khách báo sưng nhiều vùng mắt...)"
                    />

                    <div className="flex justify-end mt-4 sm:flex-row flex-col gap-2">

                        {editingId && (
                            <Button
                                onClick={() => { setEditingId(null); setNote(""); setType(DEFAULT_TYPE); }}
                            >
                                Hủy
                            </Button>
                        )}

                        <Button
                            type="primary"
                            icon={<Save size={15} />}
                            loading={saving}
                            onClick={handleSave}
                        >
                            {editingId ? "Cập nhật chăm sóc" : "Lưu chăm sóc"}
                        </Button>

                    </div>

                </Card>

            </div>

            {/* TIMELINE */}

            <Spin spinning={loading}>
                <div className="px-6 pb-6">

                    {!loading && logs.length === 0 && (
                        <div className="py-8 text-center text-slate-400 font-medium">
                            Chưa có hành trình chăm sóc
                        </div>
                    )}

                    {logs.map((item) => {
                        const meta = TYPE_META[item.type] || TYPE_META[5];
                        const TypeIcon = meta.icon;
                        return (
                            <div
                                key={item.id}
                                className="relative pl-10 pb-5"
                            >

                                {/* line */}

                                <div className="absolute left-[13px] top-8 bottom-[-22px] w-[2px] bg-green-300" />

                                {/* dot */}

                                <div
                                    className={`absolute left-0 top-1 w-7 h-7 rounded-full flex items-center justify-center border-4 border-white shadow ${meta.dot}`}
                                >
                                    <TypeIcon
                                        size={13}
                                        className="text-white"
                                    />
                                </div>

                                <div className="text-sm text-slate-400 mb-2">
                                    {formatTime(item.created_at)}
                                </div>

                                <Card
                                    className={`
                                        rounded-xl
                                        border
                                        shadow-none
                                        ${meta.border}
                                    `}
                                    styles={{
                                        body: {
                                            padding: "14px 16px",
                                        },
                                    }}
                                >
                                    <div className="flex items-start sm:items-center justify-between gap-1 sm:gap-4 sm:flex-row flex-col">

                                        <div className="flex items-center gap-1 flex-wrap">

                                            <span className={`font-semibold text-[15px] ${meta.text}`}>
                                                {item.type_label}
                                            </span>

                                        </div>

                                        <div className="flex items-center gap-1 shrink-0">

                                            <Tag
                                                className="shrink-0 rounded-md px-2 text-[11px] flex items-center gap-1 !mr-0"
                                                icon={<User size={11} />}
                                            >
                                                Người thực hiện: {item.created_by_name || "—"}
                                            </Tag>

                                            <Dropdown
                                                trigger={["click"]}
                                                menu={{
                                                    items: [
                                                        {
                                                            key: "edit",
                                                            label: "Sửa",
                                                            onClick: () => handleEdit(item),
                                                        },
                                                        {
                                                            key: "delete",
                                                            label: <span className="text-red-500">Xóa</span>,
                                                            onClick: () =>
                                                                Modal.confirm({
                                                                    title: "Xóa hành trình chăm sóc này?",
                                                                    okText: "Đồng ý",
                                                                    cancelText: "Hủy",
                                                                    okButtonProps: { danger: true },
                                                                    onOk: () => handleDelete(item),
                                                                }),
                                                        },
                                                    ],
                                                }}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<MoreVertical size={14} />}
                                                />
                                            </Dropdown>

                                        </div>

                                    </div>

                                    <div className="flex items-center gap-2 mt-2 text-[13px] italic text-slate-500">

                                        <Paperclip size={13} className="!hidden sm:!block" />
                                        {item.content}
                                    </div>

                                    {/* Ghi âm cuộc gọi — giao diện chờ API ghi âm, sẽ nối khi backend bổ sung */}
                                    {item.type === 1 && (

                                        <div className="mt-3">
                                            <AudioPlayer />
                                        </div>

                                    )}

                                </Card>

                            </div>
                        );
                    })}

                </div>
            </Spin>

        </div>
    );
}
